"use client";

import { useState } from "react";
import { Sun, Moon, Sunset, Flame, Monitor, Type, AlignJustify, MoveHorizontal, ListOrdered, PenLine, Sparkles, MessageSquare, CornerDownLeft, Users, History, Volume2, Lock, Target, Trophy, RefreshCw } from "lucide-react";

import { useAuth0 } from "@auth0/auth0-react";
import { useEditorContext } from "@/src/contexts/editorContext";
import { updateBookStats } from "@/src/helpers/dashboardDbHelper";
import { saveTheme } from "@/src/components/ThemeProvider";
import { MANUSCRIPT_MARGIN_OPTIONS, saveManuscriptMargin } from "@/src/lib/manuscriptLayout";
import { MODEL_CATALOG } from "@/src/lib/modelCatalog";
import { EDITOR_FONTS, usePreference } from "@/src/lib/preferences";
import OpenExpressionSwitch from "@/src/components/Buttons/OpenExpressionSwitch";
import { Segment } from "@/src/components/ui/segment";
import { Switch } from "@/src/components/ui/switch";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/src/components/ui/dropdown-menu";

// Section label with the caps-mono Quarkle treatment.
export function SectionLabel({ children }) {
  return (
    <div className="mb-1.5 mt-4 flex items-center gap-2 px-3">
      <span className="qk-caps" style={{ color: "var(--qk-muted)" }}>
        {children}
      </span>
      <div className="h-px flex-1" style={{ background: "var(--qk-rule)" }} />
    </div>
  );
}

// A settings row whose control sits on the right: icon, label (+ hint), control.
function PrefRow({ icon, label, hint, children }) {
  return (
    <div className="qk-settings-row qk-settings-toggle">
      <div className="flex min-w-0 items-center gap-3">
        <span className="qk-settings-row-icon">{icon}</span>
        <span className="qk-settings-row-label">
          <span className="block">{label}</span>
          {hint && <span className="qk-settings-row-hint">{hint}</span>}
        </span>
      </div>
      {children}
    </div>
  );
}

// The house menu (the toolbar's margins and layout menus use the same one),
// with a check on the current pick.
function Select({ value, onChange, options, ariaLabel, disabled = false }) {
  const current = options.find((o) => o.value === value) ?? options[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="qk-pref-select" aria-label={ariaLabel} disabled={disabled}>
        {current.label}
      </DropdownMenuTrigger>
      <DropdownMenuContent className="qk-toolbar-menu w-44" align="end">
        {options.map(({ value: v, label }) => (
          <DropdownMenuItem key={v} onClick={() => onChange(v)}>
            {label}
            {v === value && <span className="ml-auto" aria-label="Selected">✓</span>}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const THEME_MODES = [
  { value: "paper", label: "Paper", icon: Sun },
  { value: "midnight", label: "Midnight", icon: Moon },
  { value: "dusk", label: "Dusk", icon: Sunset },
  { value: "ember", label: "Ember", icon: Flame },
  { value: "system", label: "System", icon: Monitor },
];
const LENGTHS = [{ id: "short" }, { id: "medium" }, { id: "long" }];
const PLACEMENTS = [{ id: "above" }, { id: "around" }];
const SIZES = [
  { id: "small", label: "s" },
  { id: "medium", label: "m" },
  { id: "large", label: "l" },
];
const SPEEDS = [0.8, 0.9, 1.0, 1.1, 1.2].map((s) => ({ id: s, label: s.toFixed(1) }));
const TEXT_SIZES = [
  { value: "16", label: "Small · 16" },
  { value: "18", label: "Regular · 18" },
  { value: "20", label: "Large · 20" },
  { value: "22", label: "Larger · 22" },
];
const LINE_HEIGHTS = [
  { id: 1.5, label: "1.5" },
  { id: 1.75, label: "1.75" },
  { id: 2, label: "2.0" },
];
const CHAPTER_ORDERS = [
  { value: "asc", label: "First to last" },
  { value: "desc", label: "Last to first" },
];
const WORD_STEPS = [100, 250, 500, 1000, 2000].map((n) => ({ value: String(n), label: `${n} words` }));

function ThemeControl() {
  const { theme, dispatch: editorDispatch } = useEditorContext();
  const Icon = THEME_MODES.find((t) => t.value === theme)?.icon ?? Monitor;
  return (
    <PrefRow icon={<Icon className="h-3.5 w-3.5" />} label="Theme" hint="System follows your operating system">
      <Select
        ariaLabel="Theme"
        options={THEME_MODES}
        value={theme}
        onChange={(next) => {
          saveTheme(next);
          editorDispatch({ type: "theme/set", payload: next });
        }}
      />
    </PrefRow>
  );
}

// Two scopes: Workspace is the writer's own preferences (device- and account-
// wide); Book is the open book's settings, which live on the book itself.
const SCOPES = [
  { id: "workspace", label: "Workspace" },
  { id: "book", label: "Book" },
];

export default function PreferenceSections() {
  const [scope, setScope] = useState("workspace");
  const { manuscriptMargin, subscription, dispatch: editorDispatch } = useEditorContext();
  const isPro = subscription?.subscription_type === "pro";
  const [editorFont, setEditorFont] = usePreference("editorFont");
  const [textSize, setTextSize] = usePreference("editorTextSize");
  const [lineHeight, setLineHeight] = usePreference("editorLineHeight");
  const [chapterOrder, setChapterOrder] = usePreference("chapterOrder");
  const [continueSettings, setContinueSettings] = usePreference("continueWriting");
  const [inlineContext, setInlineContext] = usePreference("inlineAiContext");
  const [defaultModel, setDefaultModel] = usePreference("defaultModel");
  const [enterSends, setEnterSends] = usePreference("enterSends");
  const [autoReview, setAutoReview] = usePreference("autoReview");
  const [autoVersioning, setAutoVersioning] = usePreference("autoVersioning");
  const [readAloud, setReadAloud] = usePreference("readAloud");

  const picker = (
    <div className="mt-3 px-3">
      <Segment options={SCOPES} value={scope} onChange={setScope} ariaLabel="Preferences scope" />
    </div>
  );
  if (scope === "book") {
    return (
      <>
        {picker}
        <BookSection />
      </>
    );
  }

  return (
    <>
      {picker}
      <SectionLabel>appearance</SectionLabel>
      <div className="flex flex-col px-1">
        <ThemeControl />
        <PrefRow icon={<Type className="h-3.5 w-3.5" />} label="Editor font" hint="Default for every chapter">
          <Select ariaLabel="Editor font" value={editorFont} onChange={setEditorFont} options={EDITOR_FONTS.map((f) => ({ value: f.value, label: f.name }))} />
        </PrefRow>
        <PrefRow icon={<Type className="h-3.5 w-3.5" />} label="Text size">
          <Select ariaLabel="Text size" value={String(textSize)} onChange={(v) => setTextSize(Number(v))} options={TEXT_SIZES} />
        </PrefRow>
        <PrefRow icon={<AlignJustify className="h-3.5 w-3.5" />} label="Line spacing">
          <Segment ariaLabel="Line spacing" options={LINE_HEIGHTS} value={lineHeight} onChange={setLineHeight} />
        </PrefRow>
        <PrefRow icon={<MoveHorizontal className="h-3.5 w-3.5" />} label="Page margins">
          <Select
            ariaLabel="Page margins"
            value={manuscriptMargin}
            onChange={(value) => {
              saveManuscriptMargin(value);
              editorDispatch({ type: "manuscriptMargin/set", payload: value });
            }}
            options={MANUSCRIPT_MARGIN_OPTIONS.map((o) => ({ value: o.value, label: o.label.replace(" margins", "") }))}
          />
        </PrefRow>
        <PrefRow icon={<ListOrdered className="h-3.5 w-3.5" />} label="Chapter list" hint="Newest on top, or the book's order">
          <Select ariaLabel="Chapter list" value={chapterOrder} onChange={setChapterOrder} options={CHAPTER_ORDERS} />
        </PrefRow>
      </div>

      <SectionLabel>highlight bar</SectionLabel>
      <div className="flex flex-col px-1">
        <PrefRow icon={<PenLine className="h-3.5 w-3.5" />} label="Continue length">
          <Segment ariaLabel="Continue length" options={LENGTHS} value={continueSettings.length} onChange={(length) => setContinueSettings({ ...continueSettings, length })} />
        </PrefRow>
        <PrefRow icon={<Sparkles className="h-3.5 w-3.5" />} label="Inline AI context" hint="Which part of the chapter rides along">
          <Segment ariaLabel="Inline AI context" options={PLACEMENTS} value={inlineContext.placement} onChange={(placement) => setInlineContext({ ...inlineContext, placement })} />
        </PrefRow>
        <PrefRow icon={<Sparkles className="h-3.5 w-3.5" />} label="Context size">
          <Segment ariaLabel="Context size" options={SIZES} value={inlineContext.size} onChange={(size) => setInlineContext({ ...inlineContext, size })} />
        </PrefRow>
      </div>

      <SectionLabel>chat</SectionLabel>
      <div className="flex flex-col px-1">
        <PrefRow
          icon={isPro ? <MessageSquare className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" style={{ color: "var(--qk-muted)" }} />}
          label="Default model"
          hint={isPro ? "New books start here" : "Pro picks the model"}
        >
          <Select ariaLabel="Default model" value={defaultModel} onChange={setDefaultModel} disabled={!isPro} options={MODEL_CATALOG.map((m) => ({ value: m.id, label: m.name }))} />
        </PrefRow>
        <PrefRow icon={<CornerDownLeft className="h-3.5 w-3.5" />} label="Send with Enter" hint={enterSends ? "Shift + Enter adds a line" : "Enter adds a line, ⌘ Enter sends"}>
          <Switch checked={enterSends} onCheckedChange={setEnterSends} aria-label="Send with Enter" />
        </PrefRow>
        <OpenExpressionSwitch />
      </div>

      <SectionLabel>review</SectionLabel>
      <div className="flex flex-col px-1">
        <PrefRow icon={<Users className="h-3.5 w-3.5" />} label="Auto-review" hint="Your reviewers read new work after you pause">
          <Switch checked={autoReview.enabled} onCheckedChange={(enabled) => setAutoReview({ ...autoReview, enabled })} aria-label="Auto-review" />
        </PrefRow>
        <PrefRow icon={<Users className="h-3.5 w-3.5" />} label="Review after" hint="New words since the last review">
          <Select ariaLabel="Review after" value={String(autoReview.words)} onChange={(v) => setAutoReview({ ...autoReview, words: Number(v) })} options={WORD_STEPS} disabled={!autoReview.enabled} />
        </PrefRow>
      </div>

      <SectionLabel>versions</SectionLabel>
      <div className="flex flex-col px-1">
        <PrefRow icon={<History className="h-3.5 w-3.5" />} label="Auto-checkpoints" hint="A version after every stretch of edits">
          <Switch checked={autoVersioning.enabled} onCheckedChange={(enabled) => setAutoVersioning({ ...autoVersioning, enabled })} aria-label="Auto-checkpoints" />
        </PrefRow>
        <PrefRow icon={<History className="h-3.5 w-3.5" />} label="Checkpoint after" hint="Words changed since the last version">
          <Select ariaLabel="Checkpoint after" value={String(autoVersioning.words)} onChange={(v) => setAutoVersioning({ ...autoVersioning, words: Number(v) })} options={WORD_STEPS} disabled={!autoVersioning.enabled} />
        </PrefRow>
      </div>

      <SectionLabel>read aloud</SectionLabel>
      <div className="flex flex-col px-1">
        <PrefRow icon={<Volume2 className="h-3.5 w-3.5" />} label="Speed" hint="Voice and language live in the Narrator dock">
          <Segment ariaLabel="Reading speed" options={SPEEDS} value={readAloud.speed} onChange={(speed) => setReadAloud({ ...readAloud, speed })} />
        </PrefRow>
      </div>
    </>
  );
}

const GOAL_MIN = 1000;
const GOAL_MAX = 1000000;

// The open book's own settings, columns on the book saved through one
// owner-only endpoint. One switch drives both background agents: the Atlas
// keeping up with the prose, and the notes keeping up with the chats.
function BookSection() {
  const { getAccessTokenSilently } = useAuth0();
  const { bookId, books, dispatch } = useEditorContext();
  const book = (books || []).find((b) => b.id === bookId);
  if (!book) return null;

  // Shown at once; the save follows. A failure logs, like the shelf's goal.
  const save = async (fields) => {
    dispatch({ type: "book/patch", payload: { id: book.id, ...fields } });
    try {
      const token = await getAccessTokenSilently();
      await updateBookStats(token, book.id, fields);
    } catch (error) {
      console.error("Failed to update book settings:", error);
    }
  };
  const autoRefresh = book.atlas_auto_refresh !== false;

  return (
    <>
      <SectionLabel>{book.title}</SectionLabel>
      <div className="flex flex-col px-1">
        <PrefRow icon={<Target className="h-3.5 w-3.5" />} label="Word count goal">
          <GoalInput value={book.target_word_count} onSave={(target_word_count) => save({ target_word_count })} />
        </PrefRow>
        <PrefRow icon={<Trophy className="h-3.5 w-3.5" />} label="Count toward milestones" hint="Its words join your totals and streaks">
          <Switch checked={book.counts_toward_stats !== false} onCheckedChange={(counts_toward_stats) => save({ counts_toward_stats })} aria-label="Count toward milestones" />
        </PrefRow>
        <PrefRow icon={<RefreshCw className="h-3.5 w-3.5" />} label="Atlas auto-refresh" hint="Updates the Story Atlas and your notes as you write and chat">
          <Switch checked={autoRefresh} onCheckedChange={(on) => save({ atlas_auto_refresh: on, chat_memory: on })} aria-label="Atlas auto-refresh" />
        </PrefRow>
      </div>
    </>
  );
}

// Saves on Enter or blur; an unchanged or unparseable value saves nothing.
function GoalInput({ value, onSave }) {
  const [draft, setDraft] = useState(String(value ?? ""));
  const commit = () => {
    const next = Math.max(GOAL_MIN, Math.min(GOAL_MAX, parseInt(draft, 10) || 0));
    setDraft(String(next));
    if (next !== value) onSave(next);
  };
  return (
    <input
      type="number"
      inputMode="numeric"
      className="qk-pref-select w-24 text-right"
      aria-label="Word count goal"
      value={draft}
      min={GOAL_MIN}
      max={GOAL_MAX}
      step={1000}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
      }}
    />
  );
}
